import { Box, type SxProps, Tab, Tabs, type Theme } from "@mui/material";
import type { ReactNode } from "react";
import { BADGE, FONT_SIZE, INK } from "../../../../../theme/tokens";
import type { SystemTab, SystemTabBadge } from "./utils";

type Props<T> = {
	tabs?: SystemTab<T>[];
	currentTab?: T;
	onTabChange?: (newTab: T) => void;
	sx?: SxProps<Theme>;
	dataTestId?: string;
	/** Annie walkthrough anchor. Declared explicitly because `Props` is closed — a hyphenated attribute
	 * passed from outside would be dropped without a type error, and the tour step would never render. */
	"data-annie-target"?: string;
};

/** Renders a tab's label with an optional trailing count-pill / status-dot indicator. */
const tabLabel = (label: string, badge?: SystemTabBadge): ReactNode => {
	if (!badge) return label;
	const color = badge.color ?? "error";
	return (
		<Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
			{label}
			<Box
				component="span"
				aria-label={badge.ariaLabel}
				role={badge.ariaLabel ? "status" : undefined}
				sx={(theme) =>
					badge.dot
						? { width: BADGE.DOT_SIZE, height: BADGE.DOT_SIZE, borderRadius: "50%", backgroundColor: theme.palette[color].main, flexShrink: 0 }
						: {
								display: "inline-flex",
								alignItems: "center",
								justifyContent: "center",
								minWidth: BADGE.PILL_SIZE,
								height: BADGE.PILL_SIZE,
								px: 0.5,
								borderRadius: BADGE.PILL_RADIUS,
								fontSize: FONT_SIZE.LABEL_XS,
								fontWeight: 600,
								lineHeight: 1,
								backgroundColor: theme.palette[color].main,
								color: theme.palette[color].contrastText,
							}
				}
			>
				{badge.dot ? null : badge.content}
			</Box>
		</Box>
	);
};

/**
 * SystemTabs managed tabs component
 * @param tabs
 * @param onTabChange
 * @param currentTab
 */
export const SystemTabs = <T,>({ tabs, onTabChange, currentTab, sx, dataTestId, "data-annie-target": annieTarget }: Props<T>) => {
	return (
		<Tabs
			data-testid={dataTestId}
			data-annie-target={annieTarget}
			value={currentTab}
			// Array form so a caller's `sx` (object OR function) is merged by MUI — a plain
			// `...sx` spread silently drops function-form sx (a function has no own enumerable keys).
			sx={[
				{
					minHeight: "36px",
					"& .MuiTab-root": {
						minHeight: "36px",
						textTransform: "none",
						color: INK.SECONDARY,
						fontSize: "14px",
						padding: "4px 8px",
						minWidth: "auto",
						"&.Mui-selected": {
							color: INK.SECONDARY,
							fontWeight: 600,
						},
					},
					"& .MuiTabs-indicator": {
						backgroundColor: INK.SECONDARY,
					},
				},
				...(Array.isArray(sx) ? sx : [sx]),
			]}
		>
			{tabs?.map((tab) => (
				<Tab
					key={String(tab.value)}
					// Per-tab id derived from the bar's, as `SystemSideRail` does for its items — a list of tabs
					// needs one per item for E2E targeting, and a caller that sets no `dataTestId` gets none.
					data-testid={dataTestId ? `${dataTestId}-${String(tab.value)}` : undefined}
					label={tabLabel(tab.label, tab.badge)}
					value={tab.value}
					disabled={tab.disabled}
					icon={tab.icon}
					iconPosition={tab.icon ? "start" : undefined}
					onClick={() => {
						if (onTabChange) {
							onTabChange(tab.value);
						}
					}}
				/>
			))}
		</Tabs>
	);
};
